import React, { useState } from 'react';
import { 
  Table, 
  TableBody, 
  TableCell, 
  TableContainer, 
  TableHead, 
  TableRow, 
  Paper,
  Typography,
  Chip,
  Box,
  TableSortLabel
} from '@mui/material';

/**
 * BugsTable component for displaying bugs in a flat table format
 * @param {Object} props - Component props
 * @param {Array} props.bugs - Array of bugs to display
 * @returns {JSX.Element} BugsTable component
 */
const BugsTable = ({ bugs }) => {
  const [order, setOrder] = useState('asc');
  const [orderBy, setOrderBy] = useState('id');

  // Function to handle sorting
  const handleRequestSort = (property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  // Function to compare values for sorting
  const descendingComparator = (a, b, orderBy) => {
    // Handle special cases
    if (orderBy === 'fixVersionDummy') {
      const valA = a[orderBy] || '';
      const valB = b[orderBy] || '';
      return valA.localeCompare(valB);
    }
    
    if (orderBy === 'iterationPath') {
      const valA = a[orderBy] ? a[orderBy].split('\\').pop() : '';
      const valB = b[orderBy] ? b[orderBy].split('\\').pop() : '';
      return valA.localeCompare(valB);
    }
    
    if (orderBy === 'team') {
      const valA = a[orderBy] ? a[orderBy].split('/').pop() : '';
      const valB = b[orderBy] ? b[orderBy].split('/').pop() : '';
      return valA.localeCompare(valB);
    }
    
    if (orderBy === 'createdDate' || orderBy === 'resolutionDate' || orderBy === 'closedDate') {
      const dateA = a[orderBy] ? new Date(a[orderBy]) : null;
      const dateB = b[orderBy] ? new Date(b[orderBy]) : null;
      
      if (!dateA && !dateB) return 0;
      if (!dateA) return 1;
      if (!dateB) return -1;
      
      return dateA - dateB;
    }
    
    if (orderBy === 'age') {
      const ageA = a[orderBy] || 0;
      const ageB = b[orderBy] || 0;
      return ageA - ageB;
    }
    
    // Default comparison
    if (b[orderBy] < a[orderBy]) {
      return -1;
    }
    if (b[orderBy] > a[orderBy]) {
      return 1;
    }
    return 0;
  };

  const getComparator = (order, orderBy) => {
    return order === 'desc'
      ? (a, b) => descendingComparator(a, b, orderBy)
      : (a, b) => -descendingComparator(a, b, orderBy);
  };

  // Sort the bugs array
  const sortedBugs = React.useMemo(() => {
    if (!bugs || bugs.length === 0) return [];
    return [...bugs].sort(getComparator(order, orderBy));
  }, [bugs, order, orderBy]);
  if (!bugs || bugs.length === 0) {
    return (
      <Paper elevation={2} style={{ padding: 16, marginTop: 16 }}>
        <Typography>No bugs found matching the selected filters.</Typography>
      </Paper>
    );
  }

  // Function to get color for state
  const getStateColor = (state) => {
    switch (state) {
      case 'Active':
        return '#4472C4'; // Blue
      case 'New':
        return '#70AD47'; // Green
      case 'In Progress':
        return '#FFC000'; // Yellow
      case 'Ready for Test':
        return '#ED7D31'; // Orange
      case 'Ready for Review':
        return '#5B9BD5'; // Light Blue
      case 'Blocked':
        return '#FF0000'; // Red
      case 'Deferred':
        return '#7030A0'; // Purple
      case 'Resolved':
        return '#00B050'; // Green
      case 'Closed':
        return '#808080'; // Gray
      default:
        return '#CCCCCC'; // Light Gray
    }
  };

  // Function to get color for priority
  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'P1':
        return '#FF0000'; // Red
      case 'P2':
        return '#FFA500'; // Orange
      case 'P3':
        return '#00B050'; // Green
      case 'P4':
        return '#0070C0'; // Blue
      default:
        return '#CCCCCC'; // Light Gray
    }
  };

  return (
    <TableContainer component={Paper}>
      <Table sx={{ minWidth: 650 }} aria-label="bugs table">
        <TableHead>
          <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
            <TableCell>
              <TableSortLabel
                active={orderBy === 'id'}
                direction={orderBy === 'id' ? order : 'asc'}
                onClick={() => handleRequestSort('id')}
              >
                ID
              </TableSortLabel>
            </TableCell>
            <TableCell>
              <TableSortLabel
                active={orderBy === 'title'}
                direction={orderBy === 'title' ? order : 'asc'}
                onClick={() => handleRequestSort('title')}
              >
                Title
              </TableSortLabel>
            </TableCell>
            <TableCell>
              <TableSortLabel
                active={orderBy === 'state'}
                direction={orderBy === 'state' ? order : 'asc'}
                onClick={() => handleRequestSort('state')}
              >
                State
              </TableSortLabel>
            </TableCell>
            <TableCell>
              <TableSortLabel
                active={orderBy === 'priority'}
                direction={orderBy === 'priority' ? order : 'asc'}
                onClick={() => handleRequestSort('priority')}
              >
                Priority
              </TableSortLabel>
            </TableCell>
            <TableCell>
              <TableSortLabel
                active={orderBy === 'fixVersionDummy'}
                direction={orderBy === 'fixVersionDummy' ? order : 'asc'}
                onClick={() => handleRequestSort('fixVersionDummy')}
              >
                Fix Version
              </TableSortLabel>
            </TableCell>
            <TableCell>
              <TableSortLabel
                active={orderBy === 'iterationPath'}
                direction={orderBy === 'iterationPath' ? order : 'asc'}
                onClick={() => handleRequestSort('iterationPath')}
              >
                Sprint
              </TableSortLabel>
            </TableCell>
            <TableCell>
              <TableSortLabel
                active={orderBy === 'assignedTo'}
                direction={orderBy === 'assignedTo' ? order : 'asc'}
                onClick={() => handleRequestSort('assignedTo')}
              >
                Assigned To
              </TableSortLabel>
            </TableCell>
            <TableCell>
              <TableSortLabel
                active={orderBy === 'team'}
                direction={orderBy === 'team' ? order : 'asc'}
                onClick={() => handleRequestSort('team')}
              >
                Team
              </TableSortLabel>
            </TableCell>
            <TableCell>
              <TableSortLabel
                active={orderBy === 'createdDate'}
                direction={orderBy === 'createdDate' ? order : 'asc'}
                onClick={() => handleRequestSort('createdDate')}
              >
                Created Date
              </TableSortLabel>
            </TableCell>
            <TableCell>
              <TableSortLabel
                active={orderBy === 'resolutionDate'}
                direction={orderBy === 'resolutionDate' ? order : 'asc'}
                onClick={() => handleRequestSort('resolutionDate')}
              >
                Resolution Date
              </TableSortLabel>
            </TableCell>
            <TableCell>
              <TableSortLabel
                active={orderBy === 'closedDate'}
                direction={orderBy === 'closedDate' ? order : 'asc'}
                onClick={() => handleRequestSort('closedDate')}
              >
                Closed Date
              </TableSortLabel>
            </TableCell>
            <TableCell>
              <TableSortLabel
                active={orderBy === 'age'}
                direction={orderBy === 'age' ? order : 'asc'}
                onClick={() => handleRequestSort('age')}
              >
                Age (Days)
              </TableSortLabel>
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {sortedBugs.map((bug) => (
            <TableRow
              key={bug.id}
              sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
            >
              <TableCell component="th" scope="row">
                <a 
                  href={`https://tr-legal.visualstudio.com/HighQ%20Tech/_workitems/edit/${bug.id}`} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  style={{ color: '#1976d2', textDecoration: 'none' }}
                >
                  {bug.id}
                </a>
              </TableCell>
              <TableCell>{bug.title}</TableCell>
              <TableCell>
                <Chip 
                  label={bug.state} 
                  size="small"
                  sx={{ 
                    backgroundColor: getStateColor(bug.state),
                    color: 'white',
                    fontWeight: 'bold'
                  }}
                />
              </TableCell>
              <TableCell>
                <Chip 
                  label={bug.priority} 
                  size="small"
                  sx={{ 
                    backgroundColor: getPriorityColor(bug.priority),
                    color: 'white',
                    fontWeight: 'bold'
                  }}
                />
              </TableCell>
              <TableCell>{bug.fixVersionDummy || '-'}</TableCell>
              <TableCell>{bug.iterationPath ? bug.iterationPath.split('\\').pop() : '-'}</TableCell>
              <TableCell>{bug.assignedTo}</TableCell>
              <TableCell>{bug.team ? bug.team.split('/').pop() : '-'}</TableCell>
              <TableCell>{new Date(bug.createdDate).toLocaleString('en-US', { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit', hour12: true })}</TableCell>
              <TableCell>{bug.resolutionDate ? new Date(bug.resolutionDate).toLocaleString('en-US', { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit', hour12: true }) : '-'}</TableCell>
              <TableCell>{bug.closedDate ? new Date(bug.closedDate).toLocaleString('en-US', { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit', hour12: true }) : '-'}</TableCell>
              <TableCell>{bug.age || '-'}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default BugsTable;
